/**
 * La nota de voz dentro de la burbuja (dirección, 28/9/2026: «permite en el
 * chat que puedas grabar audios. Y que luego el audio tenga una opción de
 * ver transcripción»). Solo cómo se ve y se oye: grabar y enviar viven en
 * pages/ChatPage.jsx, y las reglas en el servidor.
 *
 *  · EL REPRODUCTOR, COMO EN WHATSAPP: el botón redondo de reproducir (de
 *    40 px: se acierta con el dedo en el iPad), la barra para ir a un punto
 *    y la duración. Propio y no el `<audio controls>` del navegador: el de
 *    cada navegador es distinto, no sigue el tema oscuro, y con el WebM que
 *    graba Chrome enseña «Infinity» (el archivo no dice cuánto dura). La
 *    duración es la que se guardó al grabar.
 *  · EL AUDIO NO SE PIDE HASTA PULSAR (preload="none"): un historial con
 *    cincuenta notas no descarga cincuenta audios al abrir el chat. Y suena
 *    una sola a la vez: al dar a otra, la anterior se para.
 *  · «VER TRANSCRIPCIÓN»: la primera vez que alguien la pulsa la pide al
 *    servidor, que la guarda en el mensaje; después se enseña y se esconde
 *    sin volver a pedir nada. Si la IA externa está apagada en la clínica,
 *    no llama a nada y dice por qué (el motivo llega en /salas).
 *  · UN AUDIO SUBIDO (29/9/2026: «admite poder incorporar pdf y audio en el
 *    chat») es una nota más: el mismo reproductor y la misma transcripción,
 *    con su nombre encima y «Descargar» (puede venir en un formato que este
 *    navegador no reproduce).
 */
import React, { useEffect, useRef, useState } from 'react';
import { Download, Loader2, Mic, Music, Pause, Play } from 'lucide-react';
import {
  CONFIG_NOTA, duracionCorta, transcribirNota, urlNotaDeVoz,
} from '../../lib/notasVozChat';

// La que está sonando ahora, en toda la pantalla: al dar a otra, se para.
let sonandoAhora = null;

// Los caracteres que cambian la dirección del texto: como en el resto del
// chat, no se pintan.
const DIRECCION_RE = /[\u061C\u200E\u200F\u202A-\u202E\u2066-\u2069]/g;

const META = 'text-[#667781] dark:text-[#8696A0]';

const NO_SE_REPRODUCE = 'No se puede reproducir aquí. Prueba a descargarla o ábrela en Chrome o Safari actualizados.';

/** El reproductor solo (también la vista previa de la nota antes de enviarla,
 *  con su propio `textoFallo`: esa no se descarga). */
export function ReproductorNota({
  src, duracionS, testid = 'chat-nota-reproductor', textoFallo = NO_SE_REPRODUCE, icono: Icono = Mic,
}) {
  const audioRef = useRef(null);
  const [sonando, setSonando] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [actual, setActual] = useState(0);
  const [medida, setMedida] = useState(null);
  const [fallo, setFallo] = useState(false);

  // Al irse de la pantalla (o cambiar de audio), que no siga sonando.
  useEffect(() => {
    const a = audioRef.current;
    return () => {
      if (!a) return;
      try { a.pause(); } catch (e) { /* ya parado */ }
      if (sonandoAhora === a) sonandoAhora = null;
    };
  }, [src]);

  const guardada = Number(duracionS) > 0 ? Number(duracionS) : 0;
  const total = Number.isFinite(medida) && medida > 0 ? medida : guardada;

  const alternar = () => {
    const a = audioRef.current;
    if (!a) return;
    setFallo(false);
    if (!a.paused) {
      a.pause();
      return;
    }
    if (sonandoAhora && sonandoAhora !== a) {
      try { sonandoAhora.pause(); } catch (e) { /* ya parado */ }
    }
    sonandoAhora = a;
    setCargando(true);
    let promesa;
    try { promesa = a.play(); } catch (e) { setCargando(false); setFallo(true); return; }
    if (promesa && typeof promesa.catch === 'function') {
      promesa.catch((e) => {
        setCargando(false);
        // Pausar antes de que arranque también «falla» (AbortError): no es
        // un fallo del audio.
        if (e?.name !== 'AbortError') setFallo(true);
      });
    }
  };

  const ir = (e) => {
    const a = audioRef.current;
    const t = Number(e.target.value) || 0;
    setActual(t);
    try { if (a) a.currentTime = t; } catch (err) { /* el navegador aún no sabe saltar */ }
  };

  const alMedir = (e) => {
    const d = e.currentTarget?.duration;
    if (Number.isFinite(d) && d > 0) setMedida(d);
  };

  return (
    <div className="flex items-center gap-2.5 min-w-0" data-testid={testid}>
      <button type="button" onClick={alternar}
        className="shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-full bg-[#00A884] dark:bg-[#06CF9C] text-white dark:text-[#0B141A] hover:brightness-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-[#00A884]"
        aria-label={sonando ? 'Pausar la nota de voz' : 'Escuchar la nota de voz'}
        data-testid="chat-nota-play">
        {cargando && !sonando ? <Loader2 className="w-5 h-5 animate-spin" />
          : (sonando ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />)}
      </button>
      <div className="flex-1 min-w-0">
        <input type="range" min={0} max={total || 1} step={0.1} value={Math.min(actual, total || 1)}
          onChange={ir}
          className="block w-full h-6 cursor-pointer accent-[#00A884] dark:accent-[#06CF9C]"
          aria-label="Ir a un punto de la nota de voz" data-testid="chat-nota-barra" />
        <div className={`flex items-center gap-1 text-[11px] leading-none tabular-nums ${META}`}>
          <Icono className="w-3 h-3" aria-hidden="true" />
          <span data-testid="chat-nota-duracion">
            {sonando || actual > 0 ? duracionCorta(actual) : duracionCorta(total)}
          </span>
        </div>
        {fallo && (
          <div className="mt-1 text-[12px] text-red-700 dark:text-red-300" role="alert" data-testid="chat-nota-fallo">
            {textoFallo}
          </div>
        )}
      </div>
      <audio ref={audioRef} src={src} preload="none" className="hidden"
        onPlay={() => setSonando(true)}
        onPlaying={() => { setCargando(false); setSonando(true); }}
        onPause={() => { setSonando(false); setCargando(false); }}
        onWaiting={() => setCargando(true)}
        onEnded={() => { setSonando(false); setCargando(false); setActual(0); }}
        onTimeUpdate={(e) => setActual(e.currentTarget.currentTime || 0)}
        onLoadedMetadata={alMedir}
        onDurationChange={alMedir}
        onError={() => { setCargando(false); setSonando(false); setFallo(true); }}
        data-testid="chat-nota-audio" />
    </div>
  );
}

/** La nota de voz de un mensaje: el reproductor y «Ver transcripción». */
export default function NotaDeVoz({ sala, audio, src, transcripcionDisponible, onTranscrita }) {
  const [abierta, setAbierta] = useState(false);
  const [pidiendo, setPidiendo] = useState(false);
  const [error, setError] = useState('');
  const texto = audio?.transcripcion;
  const tiene = typeof texto === 'string';
  // Para no seguir esperando (ni pintar) si la burbuja ya no está.
  const vivaRef = useRef(true);
  useEffect(() => {
    vivaRef.current = true;
    return () => { vivaRef.current = false; };
  }, []);

  const pulsar = async () => {
    setError('');
    if (abierta) {
      setAbierta(false);
      return;
    }
    if (tiene) {
      setAbierta(true);
      return;
    }
    // La IA externa apagada (o sin motor): se dice por qué, sin llamar.
    if (transcripcionDisponible && transcripcionDisponible.disponible === false) {
      setError(transcripcionDisponible.motivo || 'La transcripción no está disponible en esta clínica.');
      return;
    }
    setPidiendo(true);
    try {
      let r = null;
      // Si otra persona la está pidiendo en este momento (409), no es un
      // fallo: se espera y se vuelve a pedir sola, y entonces llega la que
      // ha guardado la otra (sin volver a pagarla).
      for (let intento = 1; ; intento += 1) {
        try {
          r = await transcribirNota(sala, audio.id);
          break;
        } catch (e) {
          if (e?.response?.status !== 409 || intento >= CONFIG_NOTA.intentosTranscripcion
            || !vivaRef.current) throw e;
          await new Promise((listo) => { setTimeout(listo, CONFIG_NOTA.esperaTranscripcionMs); });
          if (!vivaRef.current) return;
        }
      }
      if (!vivaRef.current) return;
      const nuevo = typeof r?.transcripcion === 'string' ? r.transcripcion : '';
      if (onTranscrita) onTranscrita(nuevo);
      setAbierta(true);
    } catch (e) {
      const detalle = e?.response?.data?.detail;
      if (typeof detalle === 'string' && detalle.trim()) setError(detalle.trim());
      else if (e && !e.response) setError('No hay conexión con el servidor: no se ha podido pedir la transcripción.');
      else setError('No se ha podido transcribir ahora. Vuelve a intentarlo en un rato.');
    } finally {
      setPidiendo(false);
    }
  };

  let etiqueta = 'Ver transcripción';
  if (pidiendo) etiqueta = 'Transcribiendo…';
  else if (abierta) etiqueta = 'Ocultar transcripción';

  // Un audio subido trae su nombre (29/9/2026); una nota grabada, no.
  const nombre = audio?.nombre ? String(audio.nombre).replace(DIRECCION_RE, '') : '';

  return (
    <div className="min-w-0" data-testid="chat-nota">
      {nombre && (
        <div className="flex items-center gap-1 pb-0.5 min-w-0 text-[13px] font-medium" data-testid="chat-nota-nombre">
          <Music className={`w-3.5 h-3.5 shrink-0 ${META}`} aria-hidden="true" />
          <span className="truncate" title={nombre}>{nombre}</span>
        </div>
      )}
      <ReproductorNota src={src} duracionS={audio?.duracion_s} icono={nombre ? Music : Mic}
        textoFallo={nombre
          ? 'No se puede reproducir aquí. Descárgalo para oírlo, o ábrelo en Chrome o Safari actualizados.'
          : NO_SE_REPRODUCE} />
      {abierta && tiene && (
        <div className="mt-1.5 whitespace-pre-wrap break-words rounded-md bg-black/5 dark:bg-white/10 px-2 py-1.5 text-[13.5px] leading-[1.35]"
          data-testid="chat-nota-transcripcion">
          {texto
            ? String(texto).replace(DIRECCION_RE, '')
            : <span className={`italic ${META}`}>No se ha entendido nada en esta nota de voz.</span>}
        </div>
      )}
      {error && (
        <div className="mt-1.5 text-[12.5px] leading-snug text-red-700 dark:text-red-300" role="alert"
          data-testid="chat-nota-transcripcion-error">
          {error}
        </div>
      )}
      {/* La fila de abajo: el botón a la izquierda y, a la derecha, el sitio
          de la hora de la burbuja (que va encima, en su esquina). */}
      <div className="mt-1 flex items-center gap-2">
        <button type="button" onClick={pulsar} disabled={pidiendo}
          className="inline-flex items-center gap-1 rounded px-1 -mx-1 py-0.5 text-[12.5px] font-medium text-[#027EB5] dark:text-[#53BDEB] hover:underline disabled:opacity-70 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00A884]"
          aria-expanded={abierta}
          data-testid="chat-nota-ver-transcripcion">
          {pidiendo && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          {etiqueta}
        </button>
        {nombre && (
          <a href={urlNotaDeVoz(sala, audio.id, { descargar: true })} download={nombre}
            className="inline-flex items-center gap-1 rounded px-1 py-0.5 text-[12.5px] font-medium text-[#027EB5] dark:text-[#53BDEB] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00A884]"
            aria-label={`Descargar ${nombre}`} data-testid="chat-nota-descargar">
            <Download className="w-3.5 h-3.5" aria-hidden="true" />
            Descargar
          </a>
        )}
        <span className="ml-auto inline-block w-12" aria-hidden="true" />
      </div>
    </div>
  );
}
